import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import LocationPicker from '../components/LocationPicker';
import CategoryManager from '../components/CategoryManager';
import FavoriteButton from '../components/FavoriteButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BrowseServices = ({ user, onLogout }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [location, setLocation] = useState(searchParams.get('location') || '');
  const [locationCoords, setLocationCoords] = useState(() => {
    const lat = searchParams.get('lat');
    const lng = searchParams.get('lng');
    return lat && lng ? [parseFloat(lat), parseFloat(lng)] : null;
  });
  const [searchRadius, setSearchRadius] = useState(() => {
    const r = searchParams.get('radius');
    return r ? parseFloat(r) : 25;
  });
  const [minPrice, setMinPrice] = useState(searchParams.get('min_price') || '');
  const [maxPrice, setMaxPrice] = useState(searchParams.get('max_price') || '');
  const [priceUnit, setPriceUnit] = useState(searchParams.get('price_unit') || '');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [favoriteIds, setFavoriteIds] = useState([]);
  const PAGE_SIZE = 24;

  useEffect(() => {
    if (!user) { setFavoriteIds([]); return; }
    axios.get(`${API}/favorites/ids`)
      .then((res) => setFavoriteIds(res.data?.provider_ids || []))
      .catch(() => setFavoriteIds([]));
  }, [user]);

  const handleFavoriteToggle = (providerId, isFav) => {
    setFavoriteIds((prev) =>
      isFav ? [...new Set([...prev, providerId])] : prev.filter((p) => p !== providerId)
    );
  };

  // Check if user is admin
  const effectiveRole = user?.active_role || user?.role;
  const isAdmin = effectiveRole === 'admin' || user?.is_owner || user?.available_roles?.includes('admin');

  // Location-based default scope (barangay -> city -> province -> all)
  const userBarangay = user?.barangay || '';
  const userCity = user?.city || '';
  const userProvince = user?.province || '';
  const [locScope, setLocScope] = useState(userBarangay ? 'barangay' : 'all');

  const inMyBarangay = (loc) =>
    !!userBarangay && (loc || '').toLowerCase().includes(userBarangay.toLowerCase());

  const scopeLabel = () => {
    if (locScope === 'barangay') return `Barangay ${userBarangay}`;
    if (locScope === 'city') return userCity;
    if (locScope === 'province') return userProvince;
    return 'all areas';
  };

  const widenScope = () => {
    const order = ['barangay', 'city', 'province', 'all'];
    const i = order.indexOf(locScope);
    for (let j = i + 1; j < order.length; j++) {
      const s = order[j];
      if (s === 'all') { setLocScope('all'); return; }
      if (s === 'city' && userCity) { setLocScope('city'); return; }
      if (s === 'province' && userProvince) { setLocScope('province'); return; }
    }
    setLocScope('all');
  };

  // Auto-scope applies only when the user hasn't entered a manual location/map filter
  const autoLocActive = !location && !locationCoords && locScope !== 'all';

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, locScope]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchServices = async (reset = true) => {
    if (reset) setLoading(true);
    else setLoadingMore(true);
    try {
      const params = {};
      if (search) params.q = search;
      if (selectedCategory) params.category = selectedCategory;
      if (location) params.location = location;
      if (locationCoords) {
        params.lat = locationCoords[0];
        params.lng = locationCoords[1];
        params.radius = searchRadius;
      }
      if (minPrice) params.min_price = minPrice;
      if (maxPrice) params.max_price = maxPrice;
      if (priceUnit) params.price_unit = priceUnit;

      // Default to the user's barangay (strict), widen on demand
      if (!location && !locationCoords && locScope !== 'all') {
        if (locScope === 'barangay' && userBarangay) params.barangay = userBarangay;
        else if (locScope === 'city' && userCity) params.city = userCity;
        else if (locScope === 'province' && userProvince) params.province = userProvince;
      }

      // Keyset (cursor) pagination: no cursor on reset, else continue from the
      // last batch's cursor. Avoids slow deep-skip scans on large result sets.
      if (!reset && nextCursor) params.cursor = nextCursor;
      params.limit = PAGE_SIZE;
      // Barangay-first ordering hint so the server returns near-me listings first
      // (kept stable across paginated batches).
      if (userBarangay) params.user_barangay = userBarangay;

      const response = await axios.get(`${API}/search`, { params });
      const newServices = response.data.services || [];
      setServices((prev) => (reset ? newServices : [...prev, ...newServices]));
      setHasMore(!!response.data.services_has_more);
      setNextCursor(response.data.services_next_cursor || null);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      if (reset) setLoading(false);
      else setLoadingMore(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    updateFilters();
  };

  const updateFilters = () => {
    const params = {};
    if (search) params.q = search;
    if (selectedCategory) params.category = selectedCategory;
    if (location) params.location = location;
    if (locationCoords) {
      params.lat = locationCoords[0];
      params.lng = locationCoords[1];
      params.radius = searchRadius;
    }
    if (minPrice) params.min_price = minPrice;
    if (maxPrice) params.max_price = maxPrice;
    if (priceUnit) params.price_unit = priceUnit;
    setSearchParams(params);
  };

  const handleLocationSelect = (selectedLocation) => {
    if (selectedLocation) {
      setLocation(selectedLocation.name);
      setLocationCoords(selectedLocation.coordinates);
      if (selectedLocation.radius) {
        setSearchRadius(selectedLocation.radius);
      }
    } else {
      setLocation('');
      setLocationCoords(null);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setSelectedCategory('');
    setLocation('');
    setLocationCoords(null);
    setMinPrice('');
    setMaxPrice('');
    setPriceUnit('');
    setLocScope(userBarangay ? 'barangay' : 'all');
    setSearchParams({});
  };

  // Helper to render category icon
  const renderCategoryIcon = (category) => {
    if (category.icon_type === 'image' && category.icon) {
      return (
        <img 
          src={category.icon.startsWith('/api') ? `${BACKEND_URL}${category.icon}` : category.icon}
          alt={category.name}
          className="w-5 h-5 object-cover rounded inline-block mr-1"
        />
      );
    }
    return <span className="mr-1">{category.icon}</span>;
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="browse-services-page">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900" data-testid="browse-title">
            {t('browse.title')}
          </h1>
          {user && isAdmin && (
            <button
              onClick={() => setShowCategoryManager(true)}
              className="bg-gray-900 text-white px-5 py-2.5 rounded-full hover:bg-gray-800 font-medium text-sm flex items-center gap-2 transition"
              data-testid="edit-categories-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {isAdmin ? t('browse.editCategories') : t('browse.addCategory')}
            </button>
          )}
        </div>

        {/* Search Bar */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 mb-6" data-testid="search-section">
          <form onSubmit={handleSearch}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">{t('browse.searchServices')}</label>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('browse.searchPlaceholder')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="search-input"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">{t('browse.location')}</label>
                <LocationPicker
                  onLocationSelect={handleLocationSelect}
                  initialLocation={locationCoords}
                  showRadius={true}
                  radius={searchRadius}
                  onRadiusChange={setSearchRadius}
                  placeholder={t('browse.locationPlaceholder')}
                />
                {location && (
                  <p className="text-xs text-gray-500 mt-1">
                    📍 {location} {searchRadius && `(${t('browse.within')} ${searchRadius}km)`}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">{t('browse.minPrice')}</label>
                <input
                  type="number"
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                  placeholder="Min"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="min-price-input"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">{t('browse.maxPrice')}</label>
                <input
                  type="number"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  placeholder="Max"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="max-price-input"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">{t('browse.priceType')}</label>
                <select
                  value={priceUnit}
                  onChange={(e) => setPriceUnit(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="price-unit-select"
                >
                  <option value="">{t('browse.allTypes')}</option>
                  <option value="per hour">{t('browse.perHour')}</option>
                  <option value="per day">{t('browse.perDay')}</option>
                  <option value="per project">{t('browse.perProject')}</option>
                  <option value="fixed">{t('browse.fixedPrice')}</option>
                  <option value="per job">{t('browse.perJob')}</option>
                </select>
              </div>
              <div className="flex items-end gap-2">
                <button
                  type="submit"
                  className="flex-1 bg-gray-900 text-white px-6 py-2.5 rounded-full hover:bg-gray-800 font-medium transition"
                  data-testid="search-button"
                >
                  {t('common.search')}
                </button>
                <button
                  type="button"
                  onClick={clearFilters}
                  className="px-5 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-full hover:bg-gray-50 font-medium transition"
                >
                  {t('browse.clear')}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Category Filter */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 mb-8" data-testid="categories-filter">
          <label className="block text-sm font-medium text-gray-700 mb-3">{t('browse.filterByCategory')}</label>
          <select
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e.target.value);
              const params = searchParams;
              if (e.target.value) {
                params.set('category', e.target.value);
              } else {
                params.delete('category');
              }
              setSearchParams(params);
            }}
            className="w-full md:w-1/2 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg"
            data-testid="category-select"
          >
            <option value="">{t('browse.allCategories')}</option>
            {categories.map((category) => (
              <option key={category.id} value={category.name}>
                {category.icon_type === 'emoji' ? category.icon : '📁'} {category.name}
              </option>
            ))}
          </select>
        </div>

        {/* Location scope banner */}
        {autoLocActive && (
          <div className="flex items-center justify-between gap-3 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 mb-6" data-testid="location-scope-banner">
            <p className="text-sm text-blue-800 flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {t('browse.showingServicesIn')} <strong>{scopeLabel()}</strong>
            </p>
            {locScope !== 'all' && (
              <button
                onClick={widenScope}
                className="text-sm font-semibold text-blue-700 hover:text-blue-900 whitespace-nowrap"
                data-testid="widen-area-btn"
              >
                {t('browse.searchWiderArea')}
              </button>
            )}
          </div>
        )}

        {/* Services Grid */}
        {loading ? (
          <div className="text-center py-12" data-testid="loading-indicator">
            <p className="text-gray-600 text-lg">{t('browse.loadingServices')}</p>
          </div>
        ) : services.length === 0 ? (
          <div className="text-center py-12" data-testid="no-services-message">
            <p className="text-gray-600 text-lg">
              {autoLocActive ? t('browse.noServicesIn', { area: scopeLabel() }) : t('browse.noServices')}
            </p>
            {autoLocActive && locScope !== 'all' ? (
              <button onClick={widenScope} className="mt-4 inline-block bg-gray-900 text-white px-6 py-2.5 rounded-full hover:bg-gray-800 font-medium" data-testid="empty-widen-area-btn">
                {t('browse.searchWiderArea')}
              </button>
            ) : (
              <button onClick={clearFilters} className="mt-4 text-blue-600 hover:underline">
                {t('browse.clearFiltersRetry')}
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="services-grid">
            {services.map((service) => (
              <Link
                key={service.id}
                to={`/services/${service.id}`}
                className="relative bg-white rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100 hover:border-gray-200 hover:-translate-y-1"
                data-testid={`service-card-${service.id}`}
              >
                {service.provider_id && service.provider_id !== user?.id && (
                  <div className="absolute top-3 right-3 z-10">
                    <FavoriteButton
                      providerId={service.provider_id}
                      user={user}
                      initialFavorited={favoriteIds.includes(service.provider_id)}
                      onChange={handleFavoriteToggle}
                      variant="floating"
                    />
                  </div>
                )}
                {/* Service Image */}
                {service.images && service.images.length > 0 ? (
                  <div className="h-48 overflow-hidden">
                    <img
                      src={service.images[0].startsWith('http') ? service.images[0] : `${BACKEND_URL}${service.images[0]}`}
                      alt={service.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                ) : (
                  <div className="h-48 bg-gray-100 flex items-center justify-center">
                    <svg className="w-16 h-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
                
                <div className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="bg-gray-100 text-gray-700 text-sm px-3 py-1 rounded-full font-medium">
                      {service.category}
                    </span>
                    {service.rating > 0 && (
                      <div className="flex items-center gap-1">
                        <svg className="w-4 h-4 text-yellow-500 fill-current" viewBox="0 0 20 20">
                          <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                        </svg>
                        <span className="text-sm font-semibold text-gray-700">{service.rating}</span>
                      </div>
                    )}
                  </div>
                  {inMyBarangay(service.location) && (
                    <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 text-xs font-semibold px-2.5 py-1 rounded-full mb-2" data-testid={`in-barangay-badge-${service.id}`}>
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {t('browse.inYourBarangay')}
                    </span>
                  )}
                  <h3 className="text-lg font-bold mb-2 text-gray-900">{service.title}</h3>
                  <p className="text-gray-500 mb-4 line-clamp-2 text-sm">{service.description}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {/* Provider Avatar */}
                      {service.provider_avatar ? (
                        <img
                          src={service.provider_avatar.startsWith('http') ? service.provider_avatar : `${BACKEND_URL}${service.provider_avatar}`}
                          alt={service.provider_name}
                          className="w-9 h-9 rounded-full object-cover border-2 border-gray-100"
                        />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-gray-900 flex items-center justify-center text-white text-sm font-medium">
                          {service.provider_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium text-gray-800">{service.provider_name}</p>
                        {service.location && (
                          <p className="text-xs text-gray-400 truncate max-w-[180px] flex items-center gap-1">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            {service.location}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-gray-900">
                        {service.currency === 'PHP' ? '₱' : service.currency === 'USD' ? '$' : service.currency === 'EUR' ? '€' : service.currency}{service.price?.toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-400">{service.price_unit}</p>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Load More */}
        {!loading && hasMore && (
          <div className="flex justify-center mt-10" data-testid="load-more-container">
            <button
              onClick={() => fetchServices(false)}
              disabled={loadingMore}
              className="bg-gray-900 text-white px-8 py-3 rounded-full hover:bg-gray-800 font-medium transition disabled:opacity-60"
              data-testid="load-more-btn"
            >
              {loadingMore ? t('common.loading') : t('common.loadMore')}
            </button>
          </div>
        )}
      </div>

      {/* Category Manager Modal (Admin Only) */}
      <CategoryManager
        isOpen={showCategoryManager}
        onClose={() => setShowCategoryManager(false)}
        onCategoryChange={fetchCategories}
        isAdmin={isAdmin}
      />

      <Footer />
    </div>
  );
};

export default BrowseServices;